import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownSLineIconV7 } from '../ui/figma-icons-v7';
import { CustomCheckbox } from '../ui/CustomCheckbox';

export interface MultiSelectOption<V extends string = string> {
  value: V;
  label: string;
}

interface MultiSelectDropdownProps<V extends string = string> {
  /** Подпись-placeholder, когда ничего не выбрано (например, «Источник»). */
  placeholder: string;
  value: V[];
  onChange: (next: V[]) => void;
  options: MultiSelectOption<V>[];
  className?: string;
  /** Если true — клик по пиллу не раскрывает меню, вместо этого вызывается onDisabledClick. */
  disabled?: boolean;
  /** Колбэк при клике по задизейбленному пиллу (например, показать тост «в разработке»). */
  onDisabledClick?: () => void;
}

interface MenuPosition {
  top: number;
  left: number;
  width: number;
}

/**
 * Pill-дропдаун с мультивыбором через чекбоксы. Стиль совпадает с CustomDropdown
 * и макетом из Figma (nodeId 8668-29155). Меню рендерится в body через Portal,
 * чтобы оно не клиппилось родительским overflow и не уходило под список диалогов.
 */
export function MultiSelectDropdown<V extends string = string>({
  placeholder,
  value,
  onChange,
  options,
  className = '',
  disabled = false,
  onDisabledClick,
}: MultiSelectDropdownProps<V>) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState<MenuPosition | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const recalcPosition = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    recalcPosition();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => recalcPosition();
    const onMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        btnRef.current && !btnRef.current.contains(t) &&
        menuRef.current && !menuRef.current.contains(t)
      ) {
        setIsOpen(false);
      }
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isOpen]);

  const toggle = (v: V) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const count = value.length;
  const display = count === 0 ? placeholder : `${placeholder}: ${count}`;

  return (
    <div className={`relative ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          if (disabled) {
            onDisabledClick?.();
            return;
          }
          setIsOpen((v) => !v);
        }}
        className={`bg-white box-border flex flex-row gap-2 items-center justify-between pl-3 pr-[10px] py-[10px] rounded-[12px] border border-[#E2E4E9] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] transition-colors w-full h-10 ${
          disabled
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:bg-[#F6F8FA] cursor-pointer'
        }`}
      >
        <span className="font-['Inter:Regular',_sans-serif] font-normal text-[14px] leading-[20px] text-[#525866] text-left truncate">
          {display}
        </span>
        <ArrowDownSLineIconV7
          size={20}
          className={`w-5 h-5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {!disabled && isOpen && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth: pos.width,
            zIndex: 99999,
          }}
          className="bg-white rounded-[12px] border border-[#E2E4E9] shadow-[0px_12px_24px_0px_rgba(14,18,27,0.08)] p-1 max-h-64 overflow-y-auto whitespace-nowrap"
        >
          {options.map((opt) => (
            <div
              key={opt.value}
              className="w-full rounded-[8px] hover:bg-[#F6F8FA]"
            >
              <CustomCheckbox
                checked={value.includes(opt.value)}
                onChange={() => toggle(opt.value)}
                label={opt.label}
                className="w-full px-3 py-2"
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default MultiSelectDropdown;
